import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../custom/redux/reduxHooks';
import { logoutUser } from '../custom/redux/reducers/authReducer';
import Swal from 'sweetalert2';

// Constants
const INACTIVITY_TIMEOUT = 15 * 60 * 1000;
const WARNING_TIME = 2 * 60 * 1000;

interface UseInactivityTimerProps {
	onLogout?: () => void;
	warningTime?: number;
	timeoutDuration?: number;
}

const useInactivityTimer = ({
	onLogout,
	warningTime = WARNING_TIME,
	timeoutDuration = INACTIVITY_TIMEOUT,
}: UseInactivityTimerProps = {}) => {
	const dispatch = useAppDispatch();
	const { user } = useAppSelector((state) => state.auth);
	const isAuthenticated = !!user;
	
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const isWarningShownRef = useRef(false);

	const clearTimers = useCallback(() => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		if (warningTimeoutRef.current) {
			clearTimeout(warningTimeoutRef.current);
			warningTimeoutRef.current = null;
		}
		isWarningShownRef.current = false;
	}, []);

	const handleLogout = useCallback(() => {
		clearTimers();
		
		if (onLogout) {
			onLogout();
		} else {
			dispatch(logoutUser());
		}

		Swal.fire({
			icon: 'info',
			title: 'Session Expired',
			text: 'You have been logged out due to inactivity.',
			confirmButtonText: 'OK',
			allowOutsideClick: false,
			allowEscapeKey: false,
		});
	}, [dispatch, onLogout, clearTimers]);

	const showWarning = useCallback(() => {
		if (isWarningShownRef.current) return;
		
		isWarningShownRef.current = true;

		Swal.fire({
			icon: 'warning',
			title: 'Session Expiring Soon',
			text: `Your session will expire in ${warningTime / 60000} minutes due to inactivity. Click "Stay Logged In" to continue your session.`,
			showCancelButton: true,
			confirmButtonText: 'Stay Logged In',
			cancelButtonText: 'Logout Now',
			allowOutsideClick: false,
			allowEscapeKey: false,
			reverseButtons: true,
		}).then((result) => {
			isWarningShownRef.current = false;
			
			if (result.isConfirmed) {
				resetTimer();
			} else {
				handleLogout();
			}
		});
	}, [warningTime, handleLogout]);

	const resetTimer = useCallback(() => {
		if (!isAuthenticated || !user) return;

		clearTimers();

		warningTimeoutRef.current = setTimeout(() => {
			showWarning();
		}, timeoutDuration - warningTime);

		timeoutRef.current = setTimeout(() => {
			if (!isWarningShownRef.current) {
				handleLogout();
			}
		}, timeoutDuration);
	}, [isAuthenticated, user, timeoutDuration, warningTime, showWarning, handleLogout, clearTimers]);

	const handleActivity = useCallback(() => {
		if (isAuthenticated && user && !isWarningShownRef.current) {
			resetTimer();
		}
	}, [isAuthenticated, user, resetTimer]);

	useEffect(() => {
		if (!isAuthenticated || !user) {
			clearTimers();
			return;
		}

		const events = [
			'mousedown',
			'mousemove',
			'keypress',
			'scroll',
			'touchstart',
			'click',
		];

		events.forEach((event) => {
			document.addEventListener(event, handleActivity, { passive: true });
		});

		resetTimer();

		return () => {
			events.forEach((event) => {
				document.removeEventListener(event, handleActivity);
			});
			clearTimers();
		};
	}, [isAuthenticated, user, handleActivity, resetTimer, clearTimers]);

	useEffect(() => {
		return () => {
			clearTimers();
		};
	}, [clearTimers]);

	return {
		resetTimer,
		clearTimers,
		isActive: timeoutRef.current !== null,
	};
};

export default useInactivityTimer;
